import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export type MenuSelectOption = { value: string; label: string }

type Props = {
  value: string
  options: MenuSelectOption[]
  onChange: (value: string) => void
  ariaLabel: string
  title?: string
  /** Classes for the trigger button (size, padding, text). */
  buttonClassName?: string
  /** Classes for the wrapper (positioning, borders, background). */
  className?: string
  /** Which edge of the trigger the menu lines up with (use 'right' near the right screen edge). */
  align?: 'left' | 'right'
  /** Menu min-width class. */
  menuWidthClass?: string
}

/**
 * Select-like dropdown we can style (native `<select>` menus use the OS highlight color).
 * Listbox pattern: arrows / Home / End move, Enter or Space picks, Esc or Tab closes, letters jump.
 */
export default function MenuSelect({
  value,
  options,
  onChange,
  ariaLabel,
  title,
  buttonClassName = '',
  className = '',
  align = 'left',
  menuWidthClass = 'min-w-[10.5rem] sm:min-w-[13rem]',
}: Props) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  )
  const selected = options[selectedIndex]

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    listRef.current?.focus()
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const openList = () => {
    setActive(selectedIndex)
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    buttonRef.current?.focus()
  }

  const pick = (i: number) => {
    const opt = options[i]
    setOpen(false)
    buttonRef.current?.focus()
    if (opt && opt.value !== value) onChange(opt.value)
  }

  const onButtonKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      openList()
    }
  }

  const onListKey = (e: KeyboardEvent) => {
    const last = options.length - 1
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => Math.min(last, i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => Math.max(0, i - 1))
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(last)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        pick(active)
        break
      case 'Escape':
        e.preventDefault()
        close()
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const k = e.key.toLowerCase()
          const order = [...options.slice(active + 1), ...options.slice(0, active + 1)]
          const hit = order.find((o) => o.label.toLowerCase().startsWith(k))
          if (hit) setActive(options.indexOf(hit))
        }
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? close() : openList())}
        onKeyDown={onButtonKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${ariaLabel}: ${selected?.label ?? ''}`}
        title={title}
        className={`flex h-full w-full items-center gap-1 text-left focus:outline-none ${buttonClassName}`}
      >
        <span className="min-w-0 flex-1 truncate">{selected?.label}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-stone-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-label={ariaLabel}
          aria-activedescendant={`${id}-${active}`}
          onKeyDown={onListKey}
          className={`absolute top-full z-[70] mt-1 max-h-80 sm:mt-1.5 ${menuWidthClass} ${align === 'right' ? 'right-0' : 'left-0'} overflow-y-auto rounded-lg border border-stone-200 bg-white py-1 text-xs shadow-lg sm:rounded-xl sm:text-sm sm:shadow-xl focus:outline-none`}
        >
          {options.map((opt, i) => {
            const isSelected = i === selectedIndex
            const isActive = i === active
            return (
              <li
                key={opt.value}
                id={`${id}-${i}`}
                data-index={i}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(i)}
                className={`mx-1 flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 sm:gap-2 sm:rounded-lg sm:px-2.5 sm:py-2 ${
                  isActive ? 'bg-primary-500 text-white' : 'text-stone-800'
                } ${isSelected ? 'font-semibold' : ''}`}
              >
                <Check
                  className={`h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 ${isSelected ? '' : 'invisible'} ${
                    isActive ? 'text-white' : 'text-primary-600'
                  }`}
                  aria-hidden
                />
                <span className="truncate">{opt.label}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
